import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { IComponentTaxonomy } from './types'
import { getComponentTaxonomyVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { GET_ALL_COMPONENTS } from '../../../query/packaging-components'
import { EXPORT_COMPONENT_TAXONOMY_QUERY } from '../../../query/export-packaging-components'
import { EXPORT_MAX_ROW_COUNT, ROUTES } from '../../../const'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'

// @ts-ignore - SCSS module import
import './index.scss'

const ComponentTaxonomyGrid: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IComponentTaxonomy[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [componentTaxonomy, setComponentTaxonomy] = useState<any>(getComponentTaxonomyVars)
  const [order, setOrder] = useState([{ name: 'ASC' }])

  const { data, loading, error, refetch } = useQuery(GET_ALL_COMPONENTS, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getComponentTaxonomyVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.taxonomyPackagingComponents.pageInfo.endCursor)
    setStartCursor(data.taxonomyPackagingComponents.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      ROUTES.componentTaxonomyItem,
      'taxonomyPackagingComponents',
      'componentTaxonomy'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].name) {
      if (!loading) {
        setComponentTaxonomy({
          ...filterEnums(tableSearch, getComponentTaxonomyVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getComponentTaxonomyVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'TaxonomyPackagingComponents',
      query: EXPORT_COMPONENT_TAXONOMY_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: {
          id: { gt: 0 },
          or: [
            { name: { contains: tableSearch || '' } },
            { alias: { contains: tableSearch || '' } },
            { code: { contains: tableSearch || '' } },
            { lastUpdatedName: { contains: tableSearch || '' } },
            { state: (componentTaxonomy || getComponentTaxonomyVars).state },
          ],
        },
        order,
      },
    }),
    [order, tableSearch, componentTaxonomy]
  )

  const handleAddNew = useCallback(() => {
    navigate(`${ROUTES.componentTaxonomyItem}?itemId=new`)
  }, [navigate])

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: '/packaging/components',
                title: 'Component Taxonomy',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Component Taxonomy</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={handleAddNew}>Add Component</Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={componentTaxonomy || getComponentTaxonomyVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default ComponentTaxonomyGrid
